import React, { useRef, useState } from "react"

import SaasTooltip from "../SaasTooltip"
import CopyBox from "../CopyBox"
import TipCodeSnippets from "./TipCodeSnippets"

import "../../../style/saas-setup-tab.module.css"
import ApiKeyList from "./ApiKeyList"
import { ApiKeyResponse } from "../../../api/saas/deployment/apiKey/post"
import { SaasAppListItem } from "../../../dashboard/saas/types"

export type CreateApiKeyFunction = () => Promise<ApiKeyResponse>
export type DeleteApiKeyFunction = (apikey: string) => Promise<ApiKeyResponse>
export type DevSetupTabProps = {
  apiKeys: string[],
  host: string,
  editConfigDisabled: boolean,
  showEditDisabledTooltip?: boolean,
  editDisabledMessage?: string;
  configButtonClickAction: React.MouseEventHandler<HTMLButtonElement>,
  accessPostgreSQLDBAction: React.MouseEventHandler<HTMLButtonElement>,
  visitDocsAnalytics: React.MouseEventHandler<HTMLAnchorElement>,
  copyCredentialCallback: Function,
  connectMoreInfoCallback: Function,
  createApiKeyFunction: CreateApiKeyFunction,
  deleteApiKeyFunction: DeleteApiKeyFunction,
  appDetails: SaasAppListItem,
}

const DevSetupTab = (props: DevSetupTabProps) => {
  const tipRef = useRef(null)
  const editRef = useRef(null)
  const disableEditing = props.appDetails.disableEditing === true;

  return (
    <div className="setup-tab">
      <ul className="setup-tab__tab-info-ul">
        <li>Use this environment for development & testing purposes only. </li>
      </ul>

      <hr />

      <div className="setup-tab__connection">
        <p className="setup-tab__connection__title">
          <span className="setup-tab__connection__title__text">
            Connecting to a development instance
          </span>

          <img
            src="static/assets/dashboard/blue-question-round.png"
            alt="Connection tips"
            ref={tipRef}
            className="blue-clickable-image"
          />
        </p>

        <p className="setup-tab__connection__details">
          Put these credentials when calling SuperTokens init function on your backend:
        </p>

        <div className="setup-tab__connection__creds">
          <div className="setup-tab__connection__creds__cred">
            <p className="setup-tab__connection__creds__cred__key">Core connectionURI:</p>
            <CopyBox
              copyCallback={() => props.copyCredentialCallback("dev", "connectionURI")}
              value={props.host}
            />
          </div>
          <div className="setup-tab__connection__creds__cred">
            <p className="setup-tab__connection__creds__cred__key">Core API key:</p>
            <ApiKeyList apiKeys={props.apiKeys} 
              copyCredentialCallback={() => props.copyCredentialCallback("dev", "API key")} 
              createApiKeyFunction={props.createApiKeyFunction}
              deleteApiKeyFunction={props.deleteApiKeyFunction}
              allowEditing={!disableEditing}
              />
          </div>
        </div>

        <hr />

        <div className="setup-tab__buttons">
          <a
            href="/docs/community/recipes"
            className="yellow-button"
            target="_blank"
            onClick={props.visitDocsAnalytics}
          >
            Visit the docs
          </a>
          <div
            ref={editRef}
            style={{
              position: "relative",
              zIndex: 0,
              maxWidth: "fit-content",
            }}>
            <button
              className="white-button"
              disabled={props.editConfigDisabled}
              onClick={props.configButtonClickAction}
              style={{
                zIndex: props.showEditDisabledTooltip || props.editDisabledMessage ? -1 : undefined,
                position: "relative",
              }}
            >
              Edit configuration
            </button>
          </div>
        </div>

        <button
          onClick={props.accessPostgreSQLDBAction}
          className="db-button"
        >
          <span>Access the PostgreSQL database used by this core</span>
          <img src="static/assets/dashboard/back-arrow.png" alt="Go" />
        </button>
        <span style={{
          color: "#808080",
          fontSize: 14,
          fontStyle: "italic",

        }}>(Only available for development environment)</span>
      </div>

      <SaasTooltip
        trigger="click"
        elementRef={tipRef}
        showTooltipCallback={props.connectMoreInfoCallback}
      >
        <TipCodeSnippets />
      </SaasTooltip>

      {
        props.showEditDisabledTooltip &&
        <SaasTooltip
          trigger="hover"
          elementRef={editRef}
          minHeight="50px"
          maxWidth="300px"
          minWidth="300px"
        >
          <span>To edit the configuration of this app refer to our docs <a style={{color: "#FF9933"}} target="_blank" href="/docs/multitenancy/new-tenant#providing-additional-configuration-per-tenant">here</a></span>
        </SaasTooltip>
      }

      {
        props.editDisabledMessage &&
        <SaasTooltip
          trigger="hover"
          elementRef={editRef}
          minHeight="50px"
          maxWidth="300px"
          minWidth="300px"
        >
          <span>{props.editDisabledMessage}</span>
        </SaasTooltip>
      }
    </div>
  )
}

export default DevSetupTab
